import { useMutation, type UseMutationResult } from '@tanstack/react-query';

import APIHandler from 'config/APIHandler';
import { storageKeys } from 'config/settings.json';
import StorageHandler from 'config/StorageHandler';

const IMPERSONATION_BACKUP_TTL_MS = 86400000;

interface ImpersonationErrorResponse {
  ok: false;
  response: Response;
}

function isImpersonationError(res: unknown): res is ImpersonationErrorResponse {
  return typeof res === 'object' && res !== null && (res as { ok?: unknown }).ok === false;
}

export interface ImpersonationUser {
  id: number;
  name: string;
  email: string;
}

interface ImpersonationTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  impersonating: boolean;
  impersonated_by: number;
  user: ImpersonationUser;
}

export interface ImpersonateResult {
  token: string;
  user: ImpersonationUser;
}

/**
 * Fetches an impersonation token for the target email and backs up the admin's current session
 * (once). Does NOT swap the active accessToken/user yet — the caller previews the target user
 * with the returned token first, then commits via `useConfirmImpersonation`.
 */
export function useImpersonate(): UseMutationResult<ImpersonateResult, Error, string> {
  return useMutation({
    mutationFn: async (email: string): Promise<ImpersonateResult> => {
      const currentAccessToken = (await StorageHandler.load(storageKeys.accessToken)) as string | false;
      if (!currentAccessToken) {
        throw new Error('Cannot impersonate without an active session');
      }

      const alreadyBackedUp = (await StorageHandler.exists(storageKeys.impersonationOriginalAccessToken)) as boolean;
      if (!alreadyBackedUp) {
        const currentUser = (await StorageHandler.load(storageKeys.user)) as Record<string, unknown> | false;
        StorageHandler.save(
          storageKeys.impersonationOriginalAccessToken,
          currentAccessToken,
          IMPERSONATION_BACKUP_TTL_MS,
        );
        StorageHandler.save(storageKeys.impersonationOriginalUser, currentUser, IMPERSONATION_BACKUP_TTL_MS);
      }

      const res = (await APIHandler.getImpersonationToken(email, currentAccessToken)) as unknown;

      if (isImpersonationError(res) || res === false) {
        throw new Error('Failed to get impersonation token');
      }

      const { access_token: token, user } = res as ImpersonationTokenResponse;

      if (!token) {
        throw new Error('No impersonation token returned from API');
      }

      return { token, user };
    },
  });
}
